import { createId } from "@paralleldrive/cuid2";
import { CheckIcon, PencilSimpleIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { defaultLanguage, defaultSections, type Language } from "@reactive-resume/schema";
import { Input } from "@reactive-resume/ui";
import { useState } from "react";

import { useResumeStore } from "@/client/stores/resume";

export const LanguagesInlineSection = () => {
  const items = useResumeStore(
    (state) => state.resume.data.sections.languages?.items ?? defaultSections.languages.items,
  );
  const setValue = useResumeStore((state) => state.setValue);
  const [editing, setEditing] = useState<Language | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [level, setLevel] = useState(3);

  const reset = () => {
    setEditing(null);
    setName("");
    setDescription("");
    setLevel(3);
  };
  const edit = (item: Language) => {
    setEditing(item);
    setName(item.name);
    setDescription(item.description);
    setLevel(item.level);
  };
  const save = () => {
    if (!name.trim()) return;
    const item = {
      ...defaultLanguage,
      ...editing,
      id: editing?.id ?? createId(),
      name: name.trim(),
      description: description.trim(),
      level,
      visible: true,
    } as Language;
    setValue(
      "sections.languages.items",
      editing ? items.map((value) => (value.id === editing.id ? item : value)) : [...items, item],
    );
    reset();
  };

  return (
    <section className="mx-auto w-full max-w-[720px] pb-3">
      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-600">Langues</p>
      <h1 className="mt-1.5 text-2xl font-bold tracking-tight text-indigo-950 sm:text-[28px]">
        Ajoutez les langues maîtrisées
      </h1>
      <p className="mt-1.5 text-xs leading-5 text-slate-500 sm:text-sm">
        Indiquez un niveau réaliste et compréhensible par le recruteur.
      </p>

      <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_1.05fr]">
        <div className="rounded-2xl border border-indigo-100 bg-white p-4 shadow-sm">
          <h2 className="text-sm font-bold text-indigo-950">
            {editing ? "Modifier la langue" : "Nouvelle langue"}
          </h2>
          <label className="mt-4 block text-[11px] font-bold text-indigo-950">
            Langue
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Ex. Français"
              className="mt-1.5 h-10 rounded-xl text-xs"
            />
          </label>
          <label className="mt-3 block text-[11px] font-bold text-indigo-950">
            Niveau affiché
            <Input
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Ex. Langue maternelle"
              className="mt-1.5 h-10 rounded-xl text-xs"
            />
          </label>
          <label className="mt-3 block text-[11px] font-bold text-indigo-950">
            Niveau graphique : {level}/5
            <input
              type="range"
              min={1}
              max={5}
              value={level}
              onChange={(event) => setLevel(Number(event.target.value))}
              className="mt-2 w-full accent-emerald-500"
            />
          </label>
          <button
            type="button"
            onClick={save}
            disabled={!name.trim()}
            className="mt-4 inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 text-xs font-bold text-white disabled:opacity-40"
          >
            {editing ? <CheckIcon size={15} /> : <PlusIcon size={15} />}
            {editing ? "Enregistrer" : "Ajouter"}
          </button>
        </div>

        <div className="space-y-2">
          {items.map((item) => (
            <article
              key={item.id}
              className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3"
            >
              <div className="min-w-0 flex-1">
                <h3 className="truncate text-xs font-bold text-indigo-950">{item.name}</h3>
                <p className="mt-0.5 text-[10px] text-slate-500">
                  {item.description || `Niveau ${item.level}/5`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => edit(item)}
                className="grid size-8 place-items-center rounded-lg text-indigo-600 hover:bg-indigo-50"
              >
                <PencilSimpleIcon size={15} />
              </button>
              <button
                type="button"
                onClick={() =>
                  setValue(
                    "sections.languages.items",
                    items.filter((value) => value.id !== item.id),
                  )
                }
                className="grid size-8 place-items-center rounded-lg text-rose-500 hover:bg-rose-50"
              >
                <TrashIcon size={15} />
              </button>
            </article>
          ))}
          {!items.length && (
            <div className="grid min-h-40 place-items-center rounded-2xl border border-dashed border-indigo-200 bg-indigo-50/30 p-5 text-center text-xs text-slate-500">
              Aucune langue ajoutée.
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
